import { useState, useMemo, useCallback, useEffect } from "react";
import {
  Download,
  Users,
  X,
  Plus,
  Calendar,
  Building2,
  Shield,
  Trash,
  Pencil,
  User,
  Clock,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Progress } from "../../components/ui/progress";
import { Select, Pagination, Popconfirm, Tooltip } from "antd";
import { get } from "@/services/axiosUitils";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Card, CardContent } from "../../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import {
  DropdownService,
  SchemaService,
  DynamicModuleService,
  PtsAttendanceService,
} from "@/services/APIs";
import { getEmployeeId } from "@/lib/storeManager";
import {
  isEmployee,
  canAccessAttendanceAndCheckin,
} from "../../utils/rolePermissions";
import { notify } from "@/components/common/Notification";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs";

const ATTENDANCE_SCHEMA_NAME = "PTS Attendance";
const EMPLOYEE_SCHEMA_NAME = "PTS Emp";

const MONTHLY_DIFF_EXCLUDED_KEYS = new Set([
  "emp_id",
  "emp_name",
  "monthly_diff",
  "employee_id",
  "monthly_difference",
  "difference",
]);

/** Parse date-like column keys to DD-MM-YYYY (day–month–year). */
function normalizeAttendanceDateColumnKeyToDDMMYYYY(
  key: string
): string | null {
  const k = String(key).trim();

  const hyphen = k.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
  if (hyphen) {
    const d = parseInt(hyphen[1], 10);
    const mo = parseInt(hyphen[2], 10);
    const y = parseInt(hyphen[3], 10);
    if (d >= 1 && d <= 31 && mo >= 1 && mo <= 12 && y >= 1900 && y <= 2100) {
      return `${String(d).padStart(2, "0")}-${String(mo).padStart(2, "0")}-${y}`;
    }
  }

  const slash = k.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (slash) {
    const d = parseInt(slash[1], 10);
    const mo = parseInt(slash[2], 10);
    const y = parseInt(slash[3], 10);
    if (d >= 1 && d <= 31 && mo >= 1 && mo <= 12 && y >= 1900 && y <= 2100) {
      return `${String(d).padStart(2, "0")}-${String(mo).padStart(2, "0")}-${y}`;
    }
  }

  const iso = k.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (iso) {
    const y = parseInt(iso[1], 10);
    const mo = parseInt(iso[2], 10);
    const d = parseInt(iso[3], 10);
    if (d >= 1 && d <= 31 && mo >= 1 && mo <= 12 && y >= 1900 && y <= 2100) {
      return `${String(d).padStart(2, "0")}-${String(mo).padStart(2, "0")}-${y}`;
    }
  }

  return null;
}

function isMonthlyDiffDateLikeKey(key: string): boolean {
  if (MONTHLY_DIFF_EXCLUDED_KEYS.has(key)) {
    return false;
  }
  return normalizeAttendanceDateColumnKeyToDDMMYYYY(key) !== null;
}

function compareDDMMYYYYStrings(a: string, b: string): number {
  const pa = a.split("-").map(Number);
  const pb = b.split("-").map(Number);
  if (pa[2] !== pb[2]) {
    return pa[2] - pb[2];
  }
  if (pa[1] !== pb[1]) {
    return pa[1] - pb[1];
  }
  return pa[0] - pb[0];
}

/** Stops Excel from auto-converting CSV headers like 01-04-2026 into locale dates (e.g. 1/4/2026). */
function csvExcelSafeDateColumnHeader(ddmmyyyy: string): string {
  const inner = `\t${ddmmyyyy}`;
  return `"${inner.replace(/"/g, '""')}"`;
}

// Component to fetch and display employee name in tooltip
const EmployeeTooltipContent = ({
  userId,
}: {
  userId: string | null | undefined;
}) => {
  const { data: employeeData, isLoading } = useQuery({
    queryKey: ["employee-by-user-id", userId],
    queryFn: async () => {
      if (!userId) {
        return null;
      }
      try {
        // Use encodeURIComponent to properly encode the model name
        const modelName = encodeURIComponent(EMPLOYEE_SCHEMA_NAME);
        const response = await get<any>(
          `/formily/dynamic-model/${modelName}/`,
          {
            params: {
              users__eq: userId,
            },
          }
        );
        return response;
      } catch (error) {
        console.error("Error fetching employee data:", error);
        return null;
      }
    },
    enabled: !!userId,
  });

  // Extract employee name from response
  const employeeName = useMemo(() => {
    if (!employeeData) {
      return "Loading...";
    }

    // Handle different response formats
    const results =
      employeeData?.results ??
      employeeData?.data ??
      (Array.isArray(employeeData) ? employeeData : []);

    if (results.length === 0) {
      return "Unknown Employee";
    }

    const employee = results[0];

    // Try to find first_name and last_name fields
    const firstName =
      employee.first_name ??
      employee.firstname ??
      employee.fname ??
      employee.firstName ??
      "";
    const lastName =
      employee.last_name ??
      employee.lastname ??
      employee.lname ??
      employee.lastName ??
      "";

    const fullName = `${firstName} ${lastName}`.trim();
    return fullName || "Unknown Employee";
  }, [employeeData]);

  if (isLoading) {
    return <span className="text-gray-700">Loading...</span>;
  }

  return <span className="text-gray-700">{employeeName}</span>;
};

const AttendancePage = () => {
  const employeeId = getEmployeeId();
  const queryClient = useQueryClient();

  // Check user role permissions (HR and PTS_Admin have same rights for Attendance)
  const userIsEmployee = isEmployee();
  const userCanManageAttendance = canAccessAttendanceAndCheckin();

  const [startDate, setStartDate] = useState<string>(() => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  });
  const [endDate] = useState<string>("");

  // Pagination state
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(200);

  // Selected rows state for export
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());

  // Modal state for Add Manual Attendance
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [modalEmployeeId, setModalEmployeeId] = useState<string>("");
  const [modalDate, setModalDate] = useState<string>("");

  const [activeTab, setActiveTab] = useState<
    "daily" | "monthly" | "monthly-sheet"
  >("daily");
  const [attendanceReportTab, setAttendanceReportTab] = useState<
    "monthly" | "yearly"
  >("monthly");

  // Monthly report state
  const [selectedMonth, setSelectedMonth] = useState<string>(() => {
    const months = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    return months[new Date().getMonth()];
  });
  const [selectedYear, setSelectedYear] = useState<number>(
    new Date().getFullYear()
  );
  const [monthlyEmployee, setMonthlyEmployee] = useState<string>(() => {
    if (userIsEmployee && employeeId) {
      return employeeId;
    }
    return "all";
  });
  const [yearlyYear, setYearlyYear] = useState<number>(
    new Date().getFullYear()
  );
  const [yearlyEmployee, setYearlyEmployee] = useState<string>(() => {
    if (userIsEmployee && employeeId) {
      return employeeId;
    }
    return "";
  });

  // Set selected employee based on role
  // If employee role: always use their own employeeId (cannot change)
  // If admin/user role: default to "all" (can select any employee)
  const [selectedEmployee, setSelectedEmployee] = useState<string>(() => {
    if (userIsEmployee && employeeId) {
      return employeeId;
    }
    return "all"; // Default to "all" for admin/user roles
  });

  // Update selectedEmployee when employeeId is available (only for employee role)
  // For admin/user roles, allow manual selection changes
  useEffect(() => {
    if (userIsEmployee && employeeId) {
      // Lock to employee's own ID if they have employee role
      setSelectedEmployee(employeeId);
      setMonthlyEmployee(employeeId);
      setYearlyEmployee(employeeId);
    }
  }, [employeeId, userIsEmployee]);

  // Format date for API (YYYY-MM-DDTHH:mm:ss)
  // Start date: 00:00:00, End date: 23:59:59
  const formatDateForAPI = useCallback(
    (dateString: string, isEndDate: boolean = false) => {
      if (!dateString) {
        return null;
      }
      const [year, month, day] = dateString.split("-").map(Number);
      if (isEndDate) {
        return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}T23:59:59`;
      } else {
        return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}T00:00:00`;
      }
    },
    []
  );

  // Fetch employee dropdown for employee name mapping
  const { data: employeeOptions } = useQuery({
    queryKey: ["employee-dropdown", EMPLOYEE_SCHEMA_NAME],
    queryFn: () => DropdownService.getDropdownData(EMPLOYEE_SCHEMA_NAME),
    enabled: !!EMPLOYEE_SCHEMA_NAME,
  });

  // Fetch employee data to get first_name and last_name and total count
  const { data: employeeDataResponse } = useQuery({
    queryKey: ["employee-data", EMPLOYEE_SCHEMA_NAME],
    queryFn: () => {
      return DynamicModuleService.getItemsByPage(
        EMPLOYEE_SCHEMA_NAME,
        1,
        10000, // Large page size to get all employees
        {}
      );
    },
    enabled: !!EMPLOYEE_SCHEMA_NAME,
  });

  // Extract employee records
  const employeeRecords = useMemo(() => {
    if (!employeeDataResponse) {
      return [];
    }
    if (Array.isArray(employeeDataResponse)) {
      return employeeDataResponse;
    }
    if (
      employeeDataResponse.results &&
      Array.isArray(employeeDataResponse.results)
    ) {
      return employeeDataResponse.results;
    }
    return [];
  }, [employeeDataResponse]);

  const activeEmployeeRecords = useMemo(() => {
    return employeeRecords.filter((record: any) => {
      const isActive = record?.is_active ?? record?.Is_Active;

      return !(
        isActive === false ||
        isActive === "false" ||
        isActive === 0 ||
        isActive === "0"
      );
    });
  }, [employeeRecords]);

  // Extract total employee count from response
  const totalEmployeeCount = useMemo(() => {
    return activeEmployeeRecords.length;
  }, [activeEmployeeRecords]);

  // Get employee column grid data to find first_name and last_name fields
  const { data: employeeColumnGridData } = useQuery({
    queryKey: ["columnGridData", EMPLOYEE_SCHEMA_NAME],
    queryFn: () => SchemaService.getColumnGridData(EMPLOYEE_SCHEMA_NAME),
  });

  // Find employee field names
  const findEmployeeFieldName = useCallback(
    (searchTerms: string[]) => {
      if (!employeeColumnGridData?.grid) {
        return null;
      }
      for (const field of employeeColumnGridData.grid) {
        const fieldName = field.field_name.toLowerCase();
        const fieldTitle = field.field_title?.toLowerCase() ?? "";
        if (
          searchTerms.some(term => {
            const lowerTerm = term.toLowerCase();
            return (
              fieldName.includes(lowerTerm) ?? fieldTitle.includes(lowerTerm)
            );
          })
        ) {
          return field.field_name;
        }
      }
      return null;
    },
    [employeeColumnGridData]
  );

  const employeeFirstNameField = findEmployeeFieldName([
    "first_name",
    "firstname",
    "fname",
  ]);
  const employeeLastNameField = findEmployeeFieldName([
    "last_name",
    "lastname",
    "lname",
  ]);

  // Transform employee options to show first name + last name, sorted A-Z by name
  const employeeOptionsWithNames = useMemo(() => {
    if (!employeeOptions || !activeEmployeeRecords.length) {
      return [];
    }

    const options = employeeOptions
      .filter((employee: { value: string; label: string }) =>
        activeEmployeeRecords.some(
          (record: any) =>
            String(
              record?.id ?? record?.ID ?? record?.Id ?? record?._id ?? ""
            ) === String(employee.value)
        )
      )
      .map((employee: { value: string; label: string }) => {
        // Find the employee record by ID
        const employeeRecord = activeEmployeeRecords.find(
          (record: any) =>
            String(
              record?.id ?? record?.ID ?? record?.Id ?? record?._id ?? ""
            ) === String(employee.value)
        );

        if (employeeRecord) {
          // Get first name and last name from the record
          const firstName =
            (employeeFirstNameField &&
              employeeRecord[employeeFirstNameField]) ??
            employeeRecord.first_name ??
            employeeRecord.firstname ??
            employeeRecord.fname ??
            "";
          const lastName =
            (employeeLastNameField && employeeRecord[employeeLastNameField]) ??
            employeeRecord.last_name ??
            employeeRecord.lastname ??
            employeeRecord.lname ??
            "";

          // Combine first name and last name
          const fullName = `${firstName} ${lastName}`.trim();

          return {
            value: employee.value,
            label: fullName || employee.label, // Fallback to original label if names not found
          };
        }

        // Fallback to original label if employee record not found
        return employee;
      });

    // Sort by employee name A-Z (ascending)
    return [...options].sort((a, b) =>
      (a.label ?? "").localeCompare(b.label ?? "", undefined, {
        sensitivity: "base",
      })
    );
  }, [
    employeeOptions,
    activeEmployeeRecords,
    employeeFirstNameField,
    employeeLastNameField,
  ]);

  // Get column grid data to generate all columns dynamically
  const { data: columnGridData } = useQuery({
    queryKey: ["columnGridData", ATTENDANCE_SCHEMA_NAME],
    queryFn: () => SchemaService.getColumnGridData(ATTENDANCE_SCHEMA_NAME),
  });

  // Fetch attendance data
  const { data: attendanceDataResponse, isLoading } = useQuery({
    queryKey: [
      "attendance-data",
      ATTENDANCE_SCHEMA_NAME,
      startDate,
      // endDate,
      selectedEmployee,
      currentPage,
      pageSize,
    ],
    queryFn: () => {
      const paramsFilter: any = {};

      // Add date filters only if dates are selected
      if (startDate && startDate.trim() !== "") {
        const formattedStartDate = formatDateForAPI(startDate, false);
        if (formattedStartDate) {
          paramsFilter.date__eq = formattedStartDate; //date__gte
        }
      }
      if (endDate && endDate.trim() !== "") {
        const formattedEndDate = formatDateForAPI(endDate, true);
        if (formattedEndDate) {
          paramsFilter.date__lte = formattedEndDate;
        }
      }

      // Add employee ID filter if a specific employee is selected (not "all")
      if (selectedEmployee && selectedEmployee !== "all") {
        // Try different field name variations for employee filter
        paramsFilter.empname__eq = selectedEmployee;
        // Alternative field names (uncomment if needed based on your API)
        // paramsFilter.employee__eq = selectedEmployee;
        // paramsFilter.employee_id__eq = selectedEmployee;
      }

      // Add ordering as JSON array string: ordering=["-date", "empname.firstname"]
      paramsFilter.ordering = '["-date", "empname.firstname"]';

      // Use page 1, size 1000 to get all records for the selected filters for merging
      return DynamicModuleService.getItemsByPage(
        ATTENDANCE_SCHEMA_NAME,
        1,
        1000,
        paramsFilter
      );
    },
    enabled: !!ATTENDANCE_SCHEMA_NAME,
  });

  // Extract attendance data from response
  const attendanceData = useMemo(() => {
    if (!attendanceDataResponse) {
      return [];
    }
    if (Array.isArray(attendanceDataResponse)) {
      return attendanceDataResponse;
    }
    if (
      attendanceDataResponse.results &&
      Array.isArray(attendanceDataResponse.results)
    ) {
      return attendanceDataResponse.results;
    }
    return [];
  }, [attendanceDataResponse]);

  const totalCount = useMemo(() => {
    if (selectedEmployee && selectedEmployee !== "all") {
      return 1;
    }
    return totalEmployeeCount;
  }, [totalEmployeeCount, selectedEmployee]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [startDate, endDate, selectedEmployee]);

  // Clear selected rows when filters or pagination change
  useEffect(() => {
    setSelectedRows(new Set());
  }, [startDate, endDate, selectedEmployee, currentPage, pageSize]);

  // Fetch monthly attendance differences data
  const { data: monthlyDataResponse, isLoading: monthlyLoading } = useQuery({
    queryKey: [
      "monthly-attendance-data",
      selectedMonth,
      selectedYear,
      monthlyEmployee,
    ],
    queryFn: () => {
      return PtsAttendanceService.getMonthlyDifferences(
        selectedMonth,
        selectedYear,
        monthlyEmployee === "all" ? undefined : monthlyEmployee
      );
    },
    enabled:
      !!selectedMonth &&
      !!selectedYear &&
      activeTab === "monthly" &&
      attendanceReportTab === "monthly",
  });

  const { data: yearlyDataResponse, isLoading: yearlyLoading } = useQuery({
    queryKey: ["yearly-attendance-data", yearlyYear, yearlyEmployee],
    queryFn: () =>
      PtsAttendanceService.getYearlyAttendance(yearlyYear, yearlyEmployee),
    enabled:
      !!yearlyYear &&
      !!yearlyEmployee &&
      activeTab === "monthly" &&
      attendanceReportTab === "yearly",
  });

  // Monthly Attendance Sheet (employee × day grid)
  const { data: attendanceSheetResponse, isLoading: attendanceSheetLoading } =
    useQuery({
      queryKey: [
        "monthly-attendance-sheet",
        selectedMonth,
        selectedYear,
        monthlyEmployee,
      ],
      queryFn: () =>
        PtsAttendanceService.getMonthlyAttendanceSheet(
          selectedMonth,
          selectedYear,
          monthlyEmployee === "all" ? undefined : monthlyEmployee
        ),
      enabled:
        !!selectedMonth && !!selectedYear && activeTab === "monthly-sheet",
    });

  const attendanceSheetEmployees = useMemo(() => {
    const data = attendanceSheetResponse?.data;
    if (!data?.employees || !Array.isArray(data.employees)) {
      return [];
    }
    return data.employees;
  }, [attendanceSheetResponse]);

  const attendanceSheetDayColumns = useMemo(() => {
    const first = attendanceSheetEmployees[0];
    if (!first?.dates || typeof first.dates !== "object") {
      return [];
    }
    return Object.keys(first.dates);
  }, [attendanceSheetEmployees]);

  // Extract monthly records (supports response.data or response.results)
  const monthlyRecords = useMemo(() => {
    if (!monthlyDataResponse) {
      return [];
    }
    if (Array.isArray(monthlyDataResponse)) {
      return monthlyDataResponse;
    }
    if (
      monthlyDataResponse.results &&
      Array.isArray(monthlyDataResponse.results)
    ) {
      return monthlyDataResponse.results;
    }
    if (monthlyDataResponse.data && Array.isArray(monthlyDataResponse.data)) {
      return monthlyDataResponse.data;
    }
    if (monthlyDataResponse.list && Array.isArray(monthlyDataResponse.list)) {
      return monthlyDataResponse.list;
    }
    if (
      monthlyDataResponse.data?.list &&
      Array.isArray(monthlyDataResponse.data.list)
    ) {
      return monthlyDataResponse.data.list;
    }
    return [];
  }, [monthlyDataResponse]);

  const monthlyAbsentDetail = useMemo(() => {
    if (!monthlyDataResponse || typeof monthlyDataResponse !== "object") {
      return null;
    }
    return (
      monthlyDataResponse.absent_detail ??
      monthlyDataResponse.data?.absent_detail ??
      null
    );
  }, [monthlyDataResponse]);

  const monthlySummaryStats = useMemo(() => {
    const toNumber = (value: unknown): number => {
      const parsed =
        typeof value === "number"
          ? value
          : typeof value === "string"
            ? Number(value)
            : NaN;
      return Number.isFinite(parsed) ? parsed : 0;
    };

    const presentRate = Math.min(
      Math.max(toNumber(monthlyAbsentDetail?.present_rate), 0),
      100
    );
    const absentRate = Math.min(
      Math.max(toNumber(monthlyAbsentDetail?.absent_rate), 0),
      100
    );

    return {
      totalEmployees: toNumber(monthlyAbsentDetail?.total_emp),
      totalDaysUptoToday: toNumber(monthlyAbsentDetail?.total_days_upto_today),
      totalPossibleDays: toNumber(monthlyAbsentDetail?.total_possible_days),
      totalPresentDays: toNumber(
        monthlyAbsentDetail?.total_present_day_all_emp
      ),
      totalAbsentDays: toNumber(monthlyAbsentDetail?.total_absent_day_all_emp),
      presentRate,
      absentRate,
    };
  }, [monthlyAbsentDetail]);

  const yearlyAbsentDetail = useMemo(() => {
    if (!yearlyDataResponse || typeof yearlyDataResponse !== "object") {
      return null;
    }
    return (
      yearlyDataResponse.absent_detail ?? yearlyDataResponse.data?.absent_detail
    );
  }, [yearlyDataResponse]);

  const yearlySummaryStats = useMemo(() => {
    const toNumber = (value: unknown): number => {
      const parsed =
        typeof value === "number"
          ? value
          : typeof value === "string"
            ? Number(value)
            : NaN;
      return Number.isFinite(parsed) ? parsed : 0;
    };

    return {
      totalEmployees: toNumber(yearlyAbsentDetail?.total_emp),
      totalDaysUptoToday: toNumber(yearlyAbsentDetail?.total_days_upto_today),
      totalPossibleDays: toNumber(yearlyAbsentDetail?.total_possible_days),
      totalPresentDays: toNumber(yearlyAbsentDetail?.total_present_day_all_emp),
      totalAbsentDays: toNumber(yearlyAbsentDetail?.total_absent_day_all_emp),
      presentRate: Math.min(
        Math.max(toNumber(yearlyAbsentDetail?.present_rate), 0),
        100
      ),
      absentRate: Math.min(
        Math.max(toNumber(yearlyAbsentDetail?.absent_rate), 0),
        100
      ),
      employeeName:
        yearlyDataResponse?.emp_name ??
        yearlyDataResponse?.data?.emp_name ??
        employeeOptionsWithNames?.find(
          (item: { value: string; label: string }) =>
            String(item.value) === String(yearlyEmployee)
        )?.label ??
        "-",
    };
  }, [
    yearlyAbsentDetail,
    yearlyDataResponse,
    yearlyEmployee,
    employeeOptionsWithNames,
  ]);

  // Date columns: union of date-like keys across rows, display as DD-MM-YYYY, stable sort
  const monthlyDiffDateColumnMeta = useMemo(() => {
    const keySet = new Set<string>();
    for (const record of monthlyRecords) {
      if (!record || typeof record !== "object") {
        continue;
      }
      for (const k of Object.keys(record)) {
        if (isMonthlyDiffDateLikeKey(k)) {
          keySet.add(k);
        }
      }
    }
    const entries = [...keySet].map(rawKey => ({
      rawKey,
      display: normalizeAttendanceDateColumnKeyToDDMMYYYY(rawKey) ?? rawKey,
    }));
    entries.sort((a, b) => compareDDMMYYYYStrings(a.display, b.display));
    return entries;
  }, [monthlyRecords]);

  // Helper function to get employee name by ID
  // const getEmployeeNameById = useCallback(
  //   (id: string | number | null | undefined) => {
  //     if (!id) {
  //       return null;
  //     }
  //     return employeeOptions?.find(
  //       (item: { value: string; label: string }) =>
  //         String(item.value) === String(id)
  //     );
  //   },
  //   [employeeOptions]
  // );

  // Get employee first and last name by ID
  const getEmployeeNamesById = useCallback(
    (id: string | number | null | undefined) => {
      if (!id) {
        return { firstName: "-", lastName: "" };
      }

      const employee = activeEmployeeRecords.find(
        (record: any) =>
          String(
            record?.id ?? record?.ID ?? record?.Id ?? record?._id ?? ""
          ) === String(id)
      );

      if (!employee) {
        // Fallback to dropdown label if employee record not found
        const dropdownEmployee = employeeOptionsWithNames?.find(
          (item: { value: string; label: string }) =>
            String(item.value) === String(id)
        );
        if (dropdownEmployee?.label) {
          const nameParts = dropdownEmployee.label.split(" ");
          return {
            firstName: nameParts[0] ?? "-",
            lastName: nameParts.slice(1).join(" ") ?? "",
          };
        }
        return { firstName: "-", lastName: "" };
      }

      const firstName =
        (employeeFirstNameField && employee[employeeFirstNameField]) ??
        employee.first_name ??
        employee.firstname ??
        employee.fname ??
        "";
      const lastName =
        (employeeLastNameField && employee[employeeLastNameField]) ??
        employee.last_name ??
        employee.lastname ??
        employee.lname ??
        "";

      return {
        firstName: firstName ?? "-",
        lastName: lastName ?? "",
      };
    },
    [
      activeEmployeeRecords,
      employeeOptionsWithNames,
      employeeFirstNameField,
      employeeLastNameField,
    ]
  );

  // Find field names from schema
  const findFieldName = useCallback(
    (searchTerms: string[]) => {
      if (!columnGridData?.grid) {
        return null;
      }
      for (const field of columnGridData.grid) {
        const fieldName = field.field_name.toLowerCase();
        const fieldTitle = field.field_title?.toLowerCase() ?? "";
        if (
          searchTerms.some(
            term =>
              fieldName.includes(term.toLowerCase()) ??
              fieldTitle.includes(term.toLowerCase())
          )
        ) {
          return field.field_name; // Return actual field name from schema
        }
      }
      return null;
    },
    [columnGridData]
  );

  // Get field names
  const employeeField = findFieldName([
    "empname",
    "employee",
    "employee_id",
    "emp_id",
  ]);
  const dateField = findFieldName(["date", "attendance_date", "work_date"]);
  const inTimeField = findFieldName([
    "in_time",
    "intime",
    "check_in",
    "checkin",
  ]);
  const outTimeField = findFieldName([
    "out_time",
    "outtime",
    "check_out",
    "checkout",
  ]);
  const breakInField = findFieldName([
    "break_in",
    "breakin",
    "break_in_time",
    "breakin1",
  ]);
  const breakOutField = findFieldName([
    "break_out",
    "breakout",
    "break_out_time",
    "breakout1",
  ]);
  const breckhoursField = findFieldName([
    "breckhours",
    "break_hours",
    "breakhours",
    "total_break_hours",
  ]);
  const workinghoursField = findFieldName([
    "workinghours",
    "working_hours",
    "productive_hours",
    "productivehours",
  ]);
  const statusField = findFieldName([
    "status",
    "attendance_status",
    "present_status",
    "invalid",
  ]);

  // Helper function to format date from ISO string to DD-MM-YYYY
  const formatDate = useCallback(
    (dateString: string | null | undefined): string => {
      if (!dateString) {
        return "-";
      }
      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) {
          return "-";
        }
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
      } catch {
        return "-";
      }
    },
    []
  );

  // Format time helper - updated to match AttendanceLogTab
  const formatTime = useCallback((time: string | null | undefined) => {
    if (!time || time === "-" || time === "null" || time === "undefined") {
      return "-";
    }
    try {
      // If it's already formatted, return as is
      if (
        typeof time === "string" &&
        time.includes(":") &&
        !time.includes("T")
      ) {
        // Try to parse and format it properly
        const [hours, minutes] = time.split(":");
        const hour = parseInt(hours, 10);
        const minute = minutes || "00";
        const period = hour >= 12 ? "PM" : "AM";
        const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
        return `${String(displayHour).padStart(2, "0")}:${minute} ${period}`;
      }
      // If it's a date string, format it
      const d = new Date(time);
      // Check if date is valid
      if (isNaN(d.getTime())) {
        return "-";
      }
      return d.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    } catch {
      return "-";
    }
  }, []);

  // Helper function to format date and time for edited tooltip
  const formatDateTime = useCallback(
    (dateString: string | null | undefined) => {
      if (!dateString) {
        return "-";
      }
      try {
        const date = new Date(dateString);
        // Format: "Dec 28, 10:30 AM" or similar
        const monthNames = [
          "Jan",
          "Feb",
          "Mar",
          "Apr",
          "May",
          "Jun",
          "Jul",
          "Aug",
          "Sep",
          "Oct",
          "Nov",
          "Dec",
        ];
        const month = monthNames[date.getMonth()];
        const day = date.getDate();
        const hours = date.getHours();
        const minutes = date.getMinutes();
        const ampm = hours >= 12 ? "PM" : "AM";
        const displayHours = hours % 12 || 12;
        const displayMinutes = minutes.toString().padStart(2, "0");
        return `${month} ${day}, ${displayHours}:${displayMinutes} ${ampm}`;
      } catch {
        return dateString;
      }
    },
    []
  );

  // Helper function to get status badge styling (matching AttendanceLogTab)
  const getStatusBadgeClass = (status: string): string => {
    switch (status) {
      case "Present":
        return "rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-700";
      case "Absent":
        return "rounded-full bg-red-100 px-2 py-1 text-xs font-medium text-red-700";
      case "Invalid":
        return "rounded-full bg-yellow-100 px-2 py-1 text-xs font-medium text-yellow-700";
      default:
        return "rounded-full bg-gray-100 px-2 py-1 text-xs font-medium text-gray-700";
    }
  };

  // Calculate statistics based on total employees from PTS Emp schema
  const { totalEmployees, presentCount, absentCount, attendanceRate } =
    useMemo(() => {
      // Total employees from PTS Emp schema
      const total = totalEmployeeCount;

      // Get unique employee IDs from attendance data for the selected date range
      const employeeIdsInAttendance = new Set<string>();
      attendanceData.forEach((record: any) => {
        const employeeId =
          (employeeField && record[employeeField]) ??
          record.empname ??
          record.employee_id ??
          record.employee ??
          record.emp_id ??
          "";
        if (employeeId && employeeId !== "") {
          employeeIdsInAttendance.add(String(employeeId));
        }
      });

      // Count employees who are present (based on attendance status)
      const presentEmployeeIds = new Set<string>();
      attendanceData.forEach((record: any) => {
        // Safely get status value and convert to string
        const statusValue =
          (statusField && record[statusField]) ??
          record.status ??
          record.attendance_status ??
          "";
        const status =
          typeof statusValue === "string"
            ? statusValue.toLowerCase()
            : String(statusValue ?? "").toLowerCase();

        // Check invalid field (boolean: false = Present, true = Absent)
        const invalid = record.invalid;
        let isPresent = false;

        if (invalid !== undefined && typeof invalid === "boolean") {
          isPresent = !invalid; // false = Present, true = Absent
        } else {
          const inTime =
            (inTimeField && record[inTimeField]) ??
            record.in_time ??
            record.intime ??
            record.check_in ??
            "";
          // Consider present if status is "present" or has in_time
          isPresent =
            status === "present" ||
            status === "p" ||
            (inTime && inTime !== "-");
        }

        // If present, add employee ID to present set
        if (isPresent) {
          const employeeId =
            (employeeField && record[employeeField]) ??
            record.empname ??
            record.employee_id ??
            record.employee ??
            record.emp_id ??
            "";
          if (employeeId && employeeId !== "") {
            presentEmployeeIds.add(String(employeeId));
          }
        }
      });

      // Present count = number of unique employees marked as present
      const present = presentEmployeeIds.size;

      // Absent count = Total employees - Present employees
      // Note: This calculates employees who are absent (not in attendance or marked absent)
      // If an employee has attendance but is marked absent, they count as absent
      const absent = total - present;

      // Calculate attendance rate
      const rate = total > 0 ? ((present / total) * 100).toFixed(1) : "0.0";

      return {
        totalEmployees: total,
        presentCount: present,
        absentCount: absent,
        attendanceRate: rate,
      };
    }, [
      attendanceData,
      statusField,
      inTimeField,
      employeeField,
      totalEmployeeCount,
    ]);

  // Filter data - merged with employee records to show full roster, sorted A-Z by employee name
  const filteredData = useMemo(() => {
    if (selectedEmployee && selectedEmployee !== "all") {
      // If a specific employee is selected, just show their record from attendanceData
      // If no record found but employee is selected, show a default "Absent" mock record
      if (attendanceData.length === 0 && selectedEmployee) {
        return [
          {
            [employeeField ?? "empname"]: selectedEmployee,
            [statusField ?? "status"]: "Absent",
            [dateField ?? "date"]: startDate,
            isMock: true,
          },
        ];
      }
      return attendanceData;
    }

    // Default: Show all employees for the given date, matching with attendanceData if available
    // Use employee records sorted A-Z by name so table order is consistent
    const employeeRecordsSorted = [...activeEmployeeRecords].sort(
      (a: any, b: any) => {
        const nameA =
          (employeeFirstNameField && a[employeeFirstNameField]) ??
          a.first_name ??
          a.firstname ??
          a.fname ??
          "";
        const nameB =
          (employeeFirstNameField && b[employeeFirstNameField]) ??
          b.first_name ??
          b.firstname ??
          b.fname ??
          "";
        const lastA =
          (employeeLastNameField && a[employeeLastNameField]) ??
          a.last_name ??
          a.lastname ??
          a.lname ??
          "";
        const lastB =
          (employeeLastNameField && b[employeeLastNameField]) ??
          b.last_name ??
          b.lastname ??
          b.lname ??
          "";
        const fullA = `${nameA} ${lastA}`.trim();
        const fullB = `${nameB} ${lastB}`.trim();
        return fullA.localeCompare(fullB, undefined, { sensitivity: "base" });
      }
    );

    // Paginate employee records (already sorted A-Z)
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const pagedEmployees = employeeRecordsSorted.slice(startIndex, endIndex);

    const rows = pagedEmployees.map((emp: any) => {
      const empId = String(emp.id ?? emp.ID ?? emp.Id ?? emp._id ?? "");
      const record = attendanceData.find(
        (r: any) =>
          String(
            (employeeField && r[employeeField]) ??
              r.empname ??
              r.employee_id ??
              r.employee ??
              ""
          ) === empId
      );

      if (record) {
        return record;
      }

      // Default record for employee with no attendance entry for this day
      return {
        id: `mock-${empId}`,
        [employeeField ?? "empname"]: empId,
        [statusField ?? "status"]: "Absent",
        [dateField ?? "date"]: startDate,
        isMock: true,
      };
    });

    return rows;
  }, [
    attendanceData,
    activeEmployeeRecords,
    employeeFirstNameField,
    employeeLastNameField,
    currentPage,
    pageSize,
    selectedEmployee,
    startDate,
    employeeField,
    statusField,
    dateField,
  ]);

  // Handle row selection
  const handleRowSelect = (recordId: string, checked: boolean) => {
    setSelectedRows(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(recordId);
      } else {
        newSet.delete(recordId);
      }
      return newSet;
    });
  };

  // Handle select all
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allIds = new Set<string>(
        filteredData
          .map((record: any) => {
            return String(
              record?.id ?? record?.ID ?? record?.Id ?? record?._id ?? ""
            );
          })
          .filter((id: string) => id !== "")
      );
      setSelectedRows(allIds);
    } else {
      setSelectedRows(new Set());
    }
  };

  // Check if all rows are selected
  const allRowsSelected = useMemo(() => {
    if (filteredData.length === 0) {
      return false;
    }
    return filteredData.every((record: any) => {
      const recordId =
        record?.id ?? record?.ID ?? record?.Id ?? record?._id ?? "";
      return selectedRows.has(recordId);
    });
  }, [filteredData, selectedRows]);

  // Helper function to calculate statistics from records
  const calculateStatistics = useCallback(
    (records: any[]) => {
      const total = records.length;
      const present = records.filter((record: any) => {
        // Safely get status value and convert to string
        const statusValue =
          (statusField && record[statusField]) ??
          record.status ??
          record.attendance_status ??
          "";
        const status =
          typeof statusValue === "string"
            ? statusValue.toLowerCase()
            : String(statusValue ?? "").toLowerCase();

        // Check invalid field (boolean: false = Present, true = Absent)
        const invalid = record.invalid;
        if (invalid !== undefined && typeof invalid === "boolean") {
          return !invalid; // false = Present, true = Absent
        }

        const inTime =
          (inTimeField && record[inTimeField]) ??
          record.in_time ??
          record.intime ??
          record.check_in ??
          "";
        // Consider present if status is "present" or has in_time
        return (
          status === "present" || status === "p" || (inTime && inTime !== "-")
        );
      }).length;
      const absent = total - present;
      const rate = total > 0 ? ((present / total) * 100).toFixed(1) : "0.0";
      return {
        totalEmployees: total,
        presentCount: present,
        absentCount: absent,
        attendanceRate: rate,
      };
    },
    [statusField, inTimeField]
  );

  // Helper function to format a record for CSV export (matches on-screen columns)
  const formatRecordForCSV = useCallback(
    (record: any) => {
      const employeeId =
        (employeeField && record[employeeField]) ??
        record.empname ??
        record.employee_id ??
        record.employee ??
        record.emp_id ??
        "";
      const employeeNames = getEmployeeNamesById(employeeId);
      const employeeName =
        `${employeeNames.firstName} ${employeeNames.lastName}`.trim() || "-";

      const date =
        (dateField && record[dateField]) ??
        record.date ??
        record.attendance_date ??
        record.work_date ??
        "";

      const inTime =
        (inTimeField && record[inTimeField]) ??
        record.in_time ??
        record.intime ??
        record.check_in ??
        null;

      const outTime =
        (outTimeField && record[outTimeField]) ??
        record.out_time ??
        record.outtime ??
        record.check_out ??
        null;

      const breakIn =
        (breakInField && record[breakInField]) ??
        record.break_in ??
        record.breakin ??
        record.break_in_time ??
        record.breakin1 ??
        null;

      const breakOut =
        (breakOutField && record[breakOutField]) ??
        record.break_out ??
        record.breakout ??
        record.break_out_time ??
        record.breakout1 ??
        null;

      const breakHours =
        (breckhoursField && record[breckhoursField]) ??
        record.breckhours ??
        record.break_hours ??
        record.total_break_hours ??
        record.total_break_hours_worked ??
        null;

      const workingHours =
        (workinghoursField && record[workinghoursField]) ??
        record.workinghours ??
        record.working_hours ??
        record.productive_hours ??
        record.productivehours ??
        null;

      // Match on-screen status logic exactly
      const invalid = record.invalid !== undefined ? record.invalid : null;
      const statusValue =
        (statusField && record[statusField]) ??
        record.status ??
        record.attendance_status ??
        "";

      let status = "Present";
      if (invalid !== null && typeof invalid === "boolean") {
        status = invalid ? "Invalid" : "Present";
      } else if (statusValue) {
        const statusLower =
          typeof statusValue === "string"
            ? statusValue.toLowerCase()
            : String(statusValue).toLowerCase();
        status =
          statusLower === "present" ||
          statusLower === "p" ||
          (inTime && inTime !== "-" && inTime !== null)
            ? "Present"
            : "Absent";
      } else if (!inTime || inTime === "-" || inTime === null) {
        status = "Absent";
      }

      // Escape commas and quotes in CSV values
      const escapeCSV = (value: string | number | null | undefined) => {
        if (
          value === null ||
          value === undefined ||
          value === "" ||
          value === "-" ||
          value === "null" ||
          value === "undefined"
        ) {
          return "-";
        }
        const str = String(value);
        if (str.includes(",") || str.includes('"') || str.includes("\n")) {
          return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
      };

      const formattedBreakHours =
        breakHours === null || breakHours === undefined || breakHours === ""
          ? "-"
          : String(breakHours);

      const formattedWorkingHours =
        workingHours === null ||
        workingHours === undefined ||
        workingHours === ""
          ? "-"
          : `${workingHours}h`;

      return [
        escapeCSV(employeeName),
        escapeCSV(formatDate(date)),
        escapeCSV(formatTime(inTime)),
        escapeCSV(formatTime(breakIn)),
        escapeCSV(formatTime(breakOut)),
        escapeCSV(formatTime(outTime)),
        escapeCSV(formattedBreakHours),
        escapeCSV(formattedWorkingHours),
        escapeCSV(status),
      ].join(",");
    },
    [
      employeeField,
      dateField,
      inTimeField,
      outTimeField,
      breakInField,
      breakOutField,
      breckhoursField,
      workinghoursField,
      statusField,
      getEmployeeNamesById,
      formatDate,
      formatTime,
    ]
  );

  // Export selected rows to CSV
  const handleExportSelected = () => {
    if (selectedRows.size === 0) {
      return;
    }

    // Filter selected records
    const selectedRecords = filteredData.filter((record: any) => {
      const recordId = String(
        record?.id ?? record?.ID ?? record?.Id ?? record?._id ?? ""
      );
      return selectedRows.has(recordId);
    });

    // Prepare CSV data (same columns as on-screen Daily Report)
    const headers = [
      "Employee Name",
      "Date",
      "Check-In",
      "Break In",
      "Break Out",
      "Check-Out",
      "Break Hours",
      "Working Hours",
      "Status",
    ];

    const csvRows = [
      "Daily Attendance Report",
      "",
      headers.join(","),
      ...selectedRecords.map((record: any) => formatRecordForCSV(record)),
    ];

    // Create and download CSV file
    const csvContent = csvRows.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `Daily_Attendance_Report_selected_${new Date().toISOString().split("T")[0]}.csv`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clear selection after export
    setSelectedRows(new Set());
  };

  // Format hours to hours and minutes with specific format rules
  const formatHoursToHoursMinutes = useCallback(
    (hours: number | string | null | undefined): string => {
      if (hours === null || hours === undefined || hours === "-") {
        return "-";
      }

      // Check if the value is in time format (HH:MM or H:MM)
      if (typeof hours === "string" && hours.includes(":")) {
        const isNegative = hours.startsWith("-");
        const timeStr = isNegative ? hours.substring(1) : hours;
        const timeParts = timeStr.split(":");
        if (timeParts.length === 2) {
          const h = parseInt(timeParts[0], 10) || 0;
          const m = parseInt(timeParts[1], 10) || 0;

          // "00:00" → "00:00"
          if (h === 0 && m === 0) {
            return "00:00";
          }
          // "00:15" → "00:15m" or "-00:15" → "-00:15m"
          else if (h === 0) {
            return `${isNegative ? "-" : ""}00:${String(m).padStart(2, "0")}m`;
          }
          // "02:00" → "02:00h" or "-02:00" → "-02:00h"
          else if (m === 0) {
            return `${isNegative ? "-" : ""}${String(h).padStart(2, "0")}:00h`;
          }
          // "02:15" → "2h 15m" (remove leading zero from hours)
          else {
            return `${isNegative ? "-" : ""}${h}h ${m}m`;
          }
        }
      }

      // Handle decimal hours format
      const hoursNum =
        typeof hours === "string"
          ? parseFloat(hours.replace("h", "").trim())
          : typeof hours === "number"
            ? hours
            : 0;

      if (isNaN(hoursNum) || hoursNum === 0) {
        return "-";
      }

      const isNegative = hoursNum < 0;
      const absHours = Math.abs(hoursNum);
      const wholeHours = Math.floor(absHours);
      const minutes = Math.round((absHours - wholeHours) * 60);

      if (minutes === 0) {
        return `${isNegative ? "-" : ""}${wholeHours}h`;
      } else if (wholeHours === 0) {
        return `${isNegative ? "-" : ""}${minutes}m`;
      } else {
        return `${isNegative ? "-" : ""}${wholeHours}h ${minutes}m`;
      }
    },
    []
  );

  // Export monthly data to CSV
  const handleExportMonthly = () => {
    try {
      // Prepare CSV data
      const csvRows: string[] = [];

      // Add summary section
      csvRows.push("MONTHLY ATTENDANCE DIFFERENCES REPORT");
      csvRows.push(""); // Empty row for spacing
      csvRows.push(`Month,${selectedMonth}`);
      csvRows.push(`Year,${selectedYear}`);
      csvRows.push(
        `Employee,${monthlyEmployee === "all" ? "All Employees" : "Selected Employee"}`
      );
      csvRows.push(`Total Employees,${monthlySummaryStats.totalEmployees}`);
      csvRows.push(
        `Total Present Days,${monthlySummaryStats.totalPresentDays}`
      );
      csvRows.push(`Total Absent Days,${monthlySummaryStats.totalAbsentDays}`);
      csvRows.push(
        `Present Rate,${monthlySummaryStats.presentRate.toFixed(2)}%`
      );
      csvRows.push(`Absent Rate,${monthlySummaryStats.absentRate.toFixed(2)}%`);
      csvRows.push(""); // Empty row for spacing
      csvRows.push("DETAILED MONTHLY DATA (same columns as on-screen report)");
      csvRows.push(""); // Empty row for spacing

      // Escape commas and quotes in CSV values
      const escapeCSV = (value: string | null | undefined) => {
        if (!value || value === "-") {
          return "-";
        }
        const str = String(value);
        if (str.includes(",") || str.includes('"') || str.includes("\n")) {
          return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
      };

      // Match UI: Employee Name, T.W.H., then every date as DD-MM-YYYY (tab prefix avoids Excel reformat)
      const headersRow = [
        escapeCSV("Employee Name"),
        escapeCSV("T.W.H."),
        ...monthlyDiffDateColumnMeta.map(({ display }) =>
          csvExcelSafeDateColumnHeader(display)
        ),
      ].join(",");
      csvRows.push(headersRow);

      const formatMonthlyDiffCell = (monthlyDiff: unknown): string => {
        if (monthlyDiff === "-" || monthlyDiff === null || monthlyDiff === "") {
          return "-";
        }
        if (
          typeof monthlyDiff === "string" &&
          monthlyDiff.includes(":") &&
          !/^on-leave$/i.test(monthlyDiff)
        ) {
          return String(monthlyDiff);
        }
        return formatHoursToHoursMinutes(monthlyDiff as number | string);
      };

      monthlyRecords.forEach((record: any) => {
        const employeeId =
          record?.emp_id ?? record?.employee_id ?? record?.employee ?? "";
        const employeeName =
          record?.emp_name ??
          (() => {
            const names = getEmployeeNamesById(employeeId);
            return `${names.firstName} ${names.lastName}`.trim() || "-";
          })();

        const monthlyDiff =
          record?.monthly_diff ??
          record?.monthly_difference ??
          record?.difference ??
          "-";

        const twhCell = formatMonthlyDiffCell(monthlyDiff);

        const dateCells = monthlyDiffDateColumnMeta.map(({ rawKey }) => {
          const val =
            (record[rawKey] !== null && String(record[rawKey]).trim()) || "";
          return escapeCSV(val || "-");
        });

        csvRows.push(
          [escapeCSV(employeeName), escapeCSV(twhCell), ...dateCells].join(",")
        );
      });

      // Create and download CSV file
      const csvContent = csvRows.join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `monthly_attendance_report_${selectedMonth}_${selectedYear}.csv`
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error exporting monthly attendance report:", error);
      alert("Failed to export monthly attendance report. Please try again.");
    }
  };

  // Export Monthly Attendance Sheet to CSV (#, Employee Name, day columns)
  const handleExportMonthlySheet = () => {
    try {
      const csvRows: string[] = [];
      csvRows.push("MONTHLY ATTENDANCE SHEET");
      csvRows.push("");
      csvRows.push(`Month,${selectedMonth}`);
      csvRows.push(`Year,${selectedYear}`);
      csvRows.push("");
      csvRows.push("DETAILED ATTENDANCE SHEET");
      csvRows.push("");

      const escapeCSV = (value: string | number | null | undefined) => {
        if (value === null || value === undefined) {
          return "";
        }
        const str = String(value);
        if (str.includes(",") || str.includes('"') || str.includes("\n")) {
          return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
      };

      const headers = ["#", "Employee Name", ...attendanceSheetDayColumns];
      csvRows.push(headers.map(h => escapeCSV(h)).join(","));

      attendanceSheetEmployees.forEach((emp, index) => {
        const row = [
          index + 1,
          emp.empname ?? "-",
          ...attendanceSheetDayColumns.map(
            dayKey => emp.dates?.[dayKey] ?? "-"
          ),
        ];
        csvRows.push(row.map(cell => escapeCSV(cell)).join(","));
      });

      const csvContent = csvRows.join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `monthly_attendance_sheet_${selectedMonth}_${selectedYear}.csv`
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error exporting monthly attendance sheet:", error);
      alert("Failed to export monthly attendance sheet. Please try again.");
    }
  };

  const handleExportYearly = () => {
    if (!yearlyEmployee) {
      notify.error("Selection Required", "Please select an employee first");
      return;
    }

    try {
      const csvRows: string[] = [];
      csvRows.push("YEARLY ATTENDANCE REPORT");
      csvRows.push("");
      csvRows.push(`Year,${yearlyYear}`);
      csvRows.push(`Employee,${yearlySummaryStats.employeeName}`);
      csvRows.push(`Total Employees,${yearlySummaryStats.totalEmployees}`);
      csvRows.push(
        `Total Days Upto Today,${yearlySummaryStats.totalDaysUptoToday}`
      );
      csvRows.push(
        `Total Possible Days,${yearlySummaryStats.totalPossibleDays}`
      );
      csvRows.push(`Present Days,${yearlySummaryStats.totalPresentDays}`);
      csvRows.push(`Absent Days,${yearlySummaryStats.totalAbsentDays}`);
      csvRows.push(
        `Present Rate,${yearlySummaryStats.presentRate.toFixed(2)}%`
      );
      csvRows.push(`Absent Rate,${yearlySummaryStats.absentRate.toFixed(2)}%`);

      const csvContent = csvRows.join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `yearly_attendance_report_${yearlyYear}.csv`
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error exporting yearly attendance report:", error);
      alert("Failed to export yearly attendance report. Please try again.");
    }
  };

  // Export all data to CSV
  const handleExport = async () => {
    if (activeTab === "monthly-sheet") {
      handleExportMonthlySheet();
      return;
    }
    // If monthly tab is active, export monthly data
    if (activeTab === "monthly") {
      if (attendanceReportTab === "yearly") {
        handleExportYearly();
        return;
      }
      handleExportMonthly();
      return;
    }

    try {
      // Build filter parameters (same as the query)
      const paramsFilter: any = {};

      // Add date filters only if dates are selected
      if (startDate && startDate.trim() !== "") {
        const formattedStartDate = formatDateForAPI(startDate, false);
        if (formattedStartDate) {
          paramsFilter.date__eq = formattedStartDate;
        }
      }
      if (endDate && endDate.trim() !== "") {
        const formattedEndDate = formatDateForAPI(endDate, true);
        if (formattedEndDate) {
          paramsFilter.date__lte = formattedEndDate;
        }
      }

      // Add employee ID filter if a specific employee is selected (not "all")
      if (selectedEmployee && selectedEmployee !== "all") {
        paramsFilter.empname__eq = selectedEmployee;
      }

      paramsFilter.ordering = '["-date", "empname.firstname"]';

      // Fetch all data with a large page size
      const allDataResponse = await DynamicModuleService.getItemsByPage(
        ATTENDANCE_SCHEMA_NAME,
        1,
        10000, // Large page size to get all records
        paramsFilter
      );

      // Extract all records from response
      let allRecords: any[] = [];
      if (Array.isArray(allDataResponse)) {
        allRecords = allDataResponse;
      } else if (
        allDataResponse.results &&
        Array.isArray(allDataResponse.results)
      ) {
        allRecords = allDataResponse.results;
      }

      // Calculate statistics from all records
      const statistics = calculateStatistics(allRecords);

      // Prepare CSV data with statistics at the top
      const csvRows: string[] = [];

      // Add title and summary section
      csvRows.push("Daily Attendance Report");
      csvRows.push(""); // Empty row for spacing
      csvRows.push(`Total Employees,${statistics.totalEmployees}`);
      csvRows.push(`Present,${statistics.presentCount}`);
      csvRows.push(`Absent,${statistics.absentCount}`);
      csvRows.push(`Attendance Rate,${statistics.attendanceRate}%`);
      csvRows.push(""); // Empty row for spacing

      // Add headers (same columns as on-screen Daily Report)
      const headers = [
        "Employee Name",
        "Date",
        "Check-In",
        "Break In",
        "Break Out",
        "Check-Out",
        "Break Hours",
        "Working Hours",
        "Status",
      ];
      csvRows.push(headers.join(","));

      // Add data rows
      csvRows.push(
        ...allRecords.map((record: any) => formatRecordForCSV(record))
      );

      // Create and download CSV file
      const csvContent = csvRows.join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);

      // Generate filename with date range if applicable
      let filename = `Daily_Attendance_Report_${new Date().toISOString().split("T")[0]}`;
      if (startDate && endDate) {
        filename = `Daily_Attendance_Report_${startDate}_to_${endDate}`;
      } else if (startDate) {
        filename = `Daily_Attendance_Report_${startDate}`;
      } else if (endDate) {
        filename = `Daily_Attendance_Report_until_${endDate}`;
      }

      link.setAttribute("download", `${filename}.csv`);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error exporting attendance report:", error);
      alert("Failed to export attendance report. Please try again.");
    }
  };

  // Format date to YYYY-MM-DD for modal API (simple format, no time)
  const formatDateForModalAPI = useCallback((dateString: string): string => {
    if (!dateString) {
      return "";
    }
    // If already in YYYY-MM-DD format, return as is
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString;
    }
    // Otherwise, try to parse and format
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return "";
    }
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }, []);

  // Mutation for deleting attendance
  const deleteAttendanceMutation = useMutation({
    mutationFn: async (id: string) => {
      return await DynamicModuleService.deleteItem(ATTENDANCE_SCHEMA_NAME, id);
    },
    onSuccess: () => {
      notify.success("Attendance record deleted successfully");
      queryClient.invalidateQueries({
        queryKey: ["attendance-data", ATTENDANCE_SCHEMA_NAME],
      });
    },
    onError: (error: any) => {
      notify.error(
        error?.response?.data?.message ??
          error?.message ??
          "Failed to delete attendance record"
      );
    },
  });

  // Mutation for bulk deleting attendance
  const bulkDeleteAttendanceMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      // Delete all records in parallel
      const deletePromises = ids.map(id =>
        DynamicModuleService.deleteItem(ATTENDANCE_SCHEMA_NAME, id)
      );
      return await Promise.all(deletePromises);
    },
    onSuccess: () => {
      notify.success(
        `Successfully deleted ${selectedRows.size} attendance record(s)`
      );
      setSelectedRows(new Set()); // Clear selection after deletion
      queryClient.invalidateQueries({
        queryKey: ["attendance-data", ATTENDANCE_SCHEMA_NAME],
      });
    },
    onError: (error: any) => {
      notify.error(
        error?.response?.data?.message ??
          error?.message ??
          "Failed to delete attendance records"
      );
    },
  });

  // Handler for deleting attendance
  const handleDeleteAttendance = useCallback(
    (id: string | undefined) => {
      if (!id) {
        notify.error("Attendance ID is missing");
        return;
      }
      deleteAttendanceMutation.mutate(String(id));
    },
    [deleteAttendanceMutation]
  );

  // Handler for bulk deleting attendance
  const handleBulkDeleteAttendance = useCallback(() => {
    if (selectedRows.size === 0) {
      notify.error("Please select at least one record to delete");
      return;
    }

    const idsArray = Array.from(selectedRows);
    bulkDeleteAttendanceMutation.mutate(idsArray);
  }, [selectedRows, bulkDeleteAttendanceMutation]);

  // Mutation for generating attendance
  const generateAttendanceMutation = useMutation({
    mutationFn: async ({
      filterDate,
      empId,
    }: {
      filterDate: string;
      empId?: string;
    }) => {
      return await PtsAttendanceService.generateAttendance(filterDate, empId);
    },
    onSuccess: () => {
      // notify.success(
      //   "Success",
      //   "Attendance generated successfully"
      // );
      setIsModalOpen(false);
      setModalEmployeeId("");
      setModalDate("");
      // Invalidate and refetch attendance data
      queryClient.invalidateQueries({
        queryKey: ["attendance-data", ATTENDANCE_SCHEMA_NAME],
      });
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message ??
        error?.message ??
        "Failed to generate attendance. Please try again.";
      notify.error("Error", errorMessage);
    },
  });

  // Handle Add Attendance button click
  const handleAddAttendance = () => {
    if (!modalDate) {
      notify.error("Error", "Please select a date");
      return;
    }

    const formattedDate = formatDateForModalAPI(modalDate);
    if (!formattedDate) {
      notify.error("Error", "Please select a valid date");
      return;
    }

    generateAttendanceMutation.mutate({
      filterDate: formattedDate,
      empId: modalEmployeeId || undefined,
    });
  };

  // Handle modal open - set default date to today
  const handleOpenModal = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    setModalDate(`${year}-${month}-${day}`);
    // If user is employee, set their own ID
    if (userIsEmployee && employeeId) {
      setModalEmployeeId(employeeId);
    } else {
      setModalEmployeeId("");
    }
    setIsModalOpen(true);
  };

  // const handleApplyFilter = () => {
  // };

  return (
    <div className="min-h-screen space-y-6 bg-gray-50 p-4 md:p-0">
      <Tabs
        value={activeTab}
        onValueChange={v =>
          setActiveTab(v as "daily" | "monthly" | "monthly-sheet")
        }
        className="w-full"
      >
        <div className="w-full overflow-x-auto rounded-xl bg-gray-100 p-[6px] md:overflow-x-hidden">
          <TabsList className="inline-flex w-max min-w-full md:flex md:w-full">
            <TabsTrigger
              value="daily"
              className="text-primary flex shrink-0 items-center gap-2 whitespace-nowrap data-[state=active]:border-b-0 data-[state=active]:bg-white data-[state=inactive]:bg-transparent data-[state=active]:text-gray-900 data-[state=active]:shadow-md md:w-full md:min-w-0 md:flex-1 md:shrink"
            >
              <Shield size={16} />
              Daily Report
            </TabsTrigger>
            <TabsTrigger
              value="monthly"
              className="text-primary flex shrink-0 items-center gap-2 whitespace-nowrap data-[state=active]:border-b-0 data-[state=active]:bg-white data-[state=inactive]:bg-transparent data-[state=active]:text-gray-900 data-[state=active]:shadow-md md:w-full md:min-w-0 md:flex-1 md:shrink"
            >
              <Building2 size={16} />
              Attendance Report
            </TabsTrigger>
            <TabsTrigger
              value="monthly-sheet"
              className="text-primary flex shrink-0 items-center gap-2 whitespace-nowrap data-[state=active]:border-b-0 data-[state=active]:bg-white data-[state=inactive]:bg-transparent data-[state=active]:text-gray-900 data-[state=active]:shadow-md md:w-full md:min-w-0 md:flex-1 md:shrink"
            >
              <Calendar size={16} />
              Monthly Attendance Sheet
            </TabsTrigger>
          </TabsList>
        </div>
      </Tabs>

      {activeTab === "daily" && (
        <>
          {/* Page Header */}
          <div className="top-[-1.5rem] z-20 flex flex-col gap-3 rounded-lg p-2 backdrop-blur-md md:sticky md:flex-row md:items-center md:justify-between">
            <div>
              <h1 className="text-xl font-bold text-[#111827] md:text-2xl">
                Daily Attendance Report
              </h1>
              <p className="text-sm text-[#627384]">
                Daily attendance tracking and reporting
              </p>
            </div>
            <div className="flex items-center gap-2">
              {userCanManageAttendance && (
                <Button
                  onClick={handleOpenModal}
                  className="border-primary hover:bg-primary text-primary flex items-center gap-2 rounded-lg border bg-white px-4 py-2 text-sm font-semibold shadow-sm hover:text-white"
                >
                  <Plus className="h-4 w-4" />
                  Add Manual
                </Button>
              )}
              <Button
                onClick={handleExport}
                className="bg-primary hover:bg-primary/90 flex items-center gap-2 text-white"
              >
                <Download className="h-4 w-4" />
                Export Report
              </Button>
            </div>
          </div>

          {/* Filter Section */}
          <Card className="border border-gray-200 bg-white">
            <CardContent className="p-3 md:p-4">
              {/* <div className="mb-4 flex items-center gap-2">
            <Calendar className="h-4 w-4 text-gray-500" />
            <h3 className="text-sm font-medium text-gray-700">Select Date</h3>
          </div> */}
              <div className="grid grid-cols-1 gap-2 md:grid-cols-5 md:gap-4">
                <div className="space-y-2">
                  <Label htmlFor="startDate" className="text-sm text-gray-700">
                    Date
                  </Label>
                  <div className="relative">
                    <input
                      type="date"
                      id="startDate"
                      value={startDate}
                      onChange={e => setStartDate(e.target.value || "")}
                      className="w-full rounded-lg border bg-[#fafafa] px-3 py-1 pr-8 text-sm"
                    />
                    {startDate && (
                      <button
                        onClick={() => setStartDate("")}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        title="Clear date"
                        type="button"
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>
                </div>
                {/* <div className="space-y-2">
                  <Label htmlFor="endDate" className="text-sm text-gray-700">
                    End Date
                  </Label>
                  <div className="relative">
                    <input
                      type="date"
                      id="endDate"
                      value={endDate}
                      onChange={e => setEndDate(e.target.value || "")}
                      className="w-full rounded-lg border bg-[#fafafa] px-3 py-1 pr-8 text-sm"
                    />
                    {endDate && (
                      <button
                        onClick={() => setEndDate("")}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        title="Clear date"
                        type="button"
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>
                </div> */}
                {/* Employee filter - Only show for admin/user roles, hide for employee role */}
                {!userIsEmployee && (
                  <div className="space-y-2">
                    <Label htmlFor="employee" className="text-sm text-gray-700">
                      Employee
                    </Label>
                    <Select
                      id="employee"
                      value={selectedEmployee}
                      onChange={setSelectedEmployee}
                      placeholder="Select employee"
                      className="h-[2rem] w-full rounded-xl"
                      showSearch
                      filterOption={(input, option) => {
                        const label = String(option?.label ?? "");
                        return label
                          .toLowerCase()
                          .includes(input.toLowerCase());
                      }}
                      options={[
                        { value: "all", label: "All Employees" },
                        ...(employeeOptionsWithNames?.map(
                          (employee: { value: string; label: string }) => ({
                            value: employee.value,
                            label: employee.label,
                          })
                        ) ?? []),
                      ]}
                    />
                  </div>
                )}
                {/* <div className="flex items-end">
              <Button
                onClick={handleApplyFilter}
                className="text-primary hover:bg-secondary w-full rounded-lg border bg-[#fafafa] px-3 py-2 text-sm hover:text-white"
              >
                Apply Filter
              </Button>
            </div> */}
              </div>
            </CardContent>
          </Card>

          {/* Summary Statistics Cards */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Card className="border border-gray-200 bg-white">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Total Employees</p>
                    <p className="text-primary mt-1 text-2xl font-bold">
                      {totalEmployees}
                    </p>
                  </div>
                  <div className="rounded-full p-3">
                    <Users className="text-primary h-6 w-6" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border border-gray-200 bg-white">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Present</p>
                    <p className="mt-1 text-2xl font-bold text-green-600">
                      {presentCount}
                    </p>
                  </div>
                  <div className="rounded-full p-3">
                    <Users className="h-6 w-6 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border border-gray-200 bg-white">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Absent</p>
                    <p className="mt-1 text-2xl font-bold text-red-600">
                      {absentCount}
                    </p>
                  </div>
                  <div className="rounded-full p-3">
                    <Users className="h-6 w-6 text-red-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border border-gray-200 bg-white">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Attendance Rate</p>
                    <p className="text-secondary mt-1 text-2xl font-bold">
                      {attendanceRate}%
                    </p>
                  </div>
                  <div className="rounded-full p-3">
                    <Users className="text-secondary h-6 w-6" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Daily Attendance Report Table */}
          <Card className="border border-gray-200 bg-white">
            <CardContent className="p-0">
              <div className="flex flex-col gap-4 border-b border-gray-200 p-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-4">
                  <h2 className="text-primary text-lg font-bold">
                    Daily Attendance Report
                  </h2>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500">
                    {filteredData.length} Records
                  </span>
                  {selectedRows.size > 0 && (
                    <>
                      <Button
                        onClick={handleExportSelected}
                        variant="outline"
                        size="sm"
                        className="text-primary hover:bg-secondary flex items-center gap-2 border hover:text-white"
                      >
                        <Download className="h-4 w-4" />
                        Export ({selectedRows.size})
                      </Button>
                      {userCanManageAttendance && (
                        <Popconfirm
                          title={`Are you sure you want to delete ${selectedRows.size} selected attendance record(s)?`}
                          description="This action cannot be undone."
                          onConfirm={handleBulkDeleteAttendance}
                          okText="Yes, Delete"
                          cancelText="Cancel"
                          okButtonProps={{
                            danger: true,
                            loading: bulkDeleteAttendanceMutation.isPending,
                          }}
                        >
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex items-center gap-2 border-red-300 text-red-600 hover:border-red-400 hover:bg-red-50 hover:text-red-700"
                            disabled={bulkDeleteAttendanceMutation.isPending}
                          >
                            <Trash className="h-4 w-4" />
                            Delete ({selectedRows.size})
                          </Button>
                        </Popconfirm>
                      )}
                    </>
                  )}
                </div>
                {/* <Button
              onClick={handleExport}
              variant="outline"
              size="sm"
              className="text-primary hover:bg-secondary flex items-center gap-2 border hover:text-white"
            >
              <Download className="h-4 w-4" />
              Export All
            </Button> */}
              </div>

              {/* Responsive Table Container */}
              <div className="w-full overflow-x-auto">
                <Table className="min-w-[800px]">
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="w-12 text-center">
                        <input
                          type="checkbox"
                          checked={allRowsSelected}
                          onChange={e => handleSelectAll(e.target.checked)}
                          className="[&:checked]:bg-primary [&:checked]:border-primary focus:ring-primary accent-primary h-4 w-4 cursor-pointer rounded border-gray-300 focus:ring-2 [&:checked]:text-white"
                          style={{
                            accentColor: "#0c3055",
                          }}
                          title="Select all"
                        />
                      </TableHead>
                      <TableHead className="text-start font-semibold text-gray-700">
                        Employee Name
                      </TableHead>
                      <TableHead className="text-center font-semibold text-gray-700">
                        Date
                      </TableHead>
                      <TableHead className="text-center font-semibold text-gray-700">
                        Check-In
                      </TableHead>
                      <TableHead className="text-center font-semibold text-gray-700">
                        Break In
                      </TableHead>
                      <TableHead className="text-center font-semibold text-gray-700">
                        Break Out
                      </TableHead>
                      <TableHead className="text-center font-semibold text-gray-700">
                        Check-Out
                      </TableHead>
                      <TableHead className="text-center font-semibold text-gray-700">
                        Break Hours
                      </TableHead>
                      <TableHead className="text-center font-semibold text-gray-700">
                        Working Hours
                      </TableHead>
                      <TableHead className="text-center font-semibold text-gray-700">
                        Status
                      </TableHead>
                      {userCanManageAttendance && (
                        <TableHead className="text-center font-semibold text-gray-700">
                          Actions
                        </TableHead>
                      )}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {isLoading ? (
                      <TableRow>
                        <TableCell
                          colSpan={userCanManageAttendance ? 10 : 9}
                          className="py-8 text-center align-middle text-gray-500"
                        >
                          Loading attendance data...
                        </TableCell>
                      </TableRow>
                    ) : filteredData.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={userCanManageAttendance ? 10 : 9}
                          className="py-8 text-center align-middle text-gray-500"
                        >
                          No attendance records found
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredData.map((record: any) => {
                        const recordId = String(
                          record?.id ??
                            record?.ID ??
                            record?.Id ??
                            record?._id ??
                            ""
                        );
                        const employeeId =
                          (employeeField && record[employeeField]) ??
                          record.empname ??
                          record.employee_id ??
                          record.employee ??
                          record.emp_id ??
                          "";
                        const employeeNames = getEmployeeNamesById(employeeId);
                        const employeeName =
                          `${employeeNames.firstName} ${employeeNames.lastName}`.trim();

                        const date =
                          (dateField && record[dateField]) ??
                          record.date ??
                          record.attendance_date ??
                          record.work_date ??
                          "";

                        const inTime =
                          (inTimeField && record[inTimeField]) ??
                          record.in_time ??
                          record.intime ??
                          record.check_in ??
                          null;

                        const outTime =
                          (outTimeField && record[outTimeField]) ??
                          record.out_time ??
                          record.outtime ??
                          record.check_out ??
                          null;

                        const breakIn =
                          (breakInField && record[breakInField]) ??
                          record.break_in ??
                          record.breakin ??
                          record.break_in_time ??
                          record.breakin1 ??
                          null;

                        const breakOut =
                          (breakOutField && record[breakOutField]) ??
                          record.break_out ??
                          record.breakout ??
                          record.break_out_time ??
                          record.breakout1 ??
                          null;

                        const breakHours =
                          (breckhoursField && record[breckhoursField]) ??
                          record.breckhours ??
                          record.break_hours ??
                          record.total_break_hours ??
                          record.total_break_hours_worked ??
                          null;

                        const workingHours =
                          (workinghoursField && record[workinghoursField]) ??
                          record.workinghours ??
                          record.working_hours ??
                          record.productive_hours ??
                          record.productivehours ??
                          null;

                        // Determine status - check invalid field or status field
                        const invalid =
                          record.invalid !== undefined ? record.invalid : null;
                        const statusValue =
                          (statusField && record[statusField]) ??
                          record.status ??
                          record.attendance_status ??
                          "";

                        // Status logic: if invalid is false → Present, if invalid is true → Absent
                        // Or check status field
                        let status = "Present";
                        if (invalid !== null && typeof invalid === "boolean") {
                          status = invalid ? "Invalid" : "Present";
                        } else if (statusValue) {
                          // Safely convert to string before calling toLowerCase
                          const statusLower =
                            typeof statusValue === "string"
                              ? statusValue.toLowerCase()
                              : String(statusValue).toLowerCase();
                          status =
                            statusLower === "present" ||
                            statusLower === "p" ||
                            (inTime && inTime !== "-" && inTime !== null)
                              ? "Present"
                              : "Absent";
                        } else if (
                          !inTime ||
                          inTime === "-" ||
                          inTime === null
                        ) {
                          status = "I";
                        }

                        const isSelected = selectedRows.has(recordId);

                        // Check if attendance record was edited: created_by != updated_by || created_on != updated_on
                        const createdBy = record.created_by;
                        const updatedBy = record.updated_by;
                        const createdOn = record.created_on;
                        const updatedOn = record.updated_on;
                        const isEdited =
                          createdBy !== updatedBy || createdOn !== updatedOn;
                        const formattedDate = isEdited
                          ? formatDateTime(updatedOn)
                          : "";

                        return (
                          <TableRow key={recordId} className="hover:bg-gray-50">
                            <TableCell className="w-12 text-center align-middle">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={e =>
                                  handleRowSelect(recordId, e.target.checked)
                                }
                                className="[&:checked]:bg-primary [&:checked]:border-primary focus:ring-primary accent-primary h-4 w-4 cursor-pointer rounded border-gray-300 focus:ring-2 [&:checked]:text-white"
                                style={{
                                  accentColor: "#0c3055",
                                }}
                                onClick={e => e.stopPropagation()}
                              />
                            </TableCell>
                            <TableCell className="pl-4 text-start align-middle font-medium text-gray-900">
                              <div className="flex flex-col gap-1">
                                {employeeName && employeeName !== "-" ? (
                                  <Link
                                    to={`/employee/${employeeId}`}
                                    className="text-primary hover:underline"
                                    onClick={e => {
                                      e.preventDefault();
                                      console.log("View employee:", employeeId);
                                    }}
                                  >
                                    {employeeName}
                                  </Link>
                                ) : (
                                  "-"
                                )}
                                {isEdited && (
                                  <Tooltip
                                    title={
                                      <div className="space-y-1">
                                        <div className="flex items-center gap-2">
                                          <User
                                            size={14}
                                            className="text-gray-800"
                                          />
                                          <EmployeeTooltipContent
                                            userId={updatedBy}
                                          />
                                        </div>
                                        <div className="flex items-center gap-2">
                                          <Clock
                                            size={14}
                                            className="text-gray-800"
                                          />
                                          <span className="text-gray-700">
                                            {formattedDate}
                                          </span>
                                        </div>
                                      </div>
                                    }
                                    placement="bottom"
                                    overlayInnerStyle={{
                                      backgroundColor: "#ffffff",
                                      color: "#374151",
                                      borderRadius: "8px",
                                      border: "1px solid #e0e6eb",
                                      padding: "8px 12px",
                                      boxShadow:
                                        "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                                    }}
                                  >
                                    <div className="flex cursor-pointer items-center gap-1 text-xs text-gray-500 hover:text-gray-700">
                                      <Pencil size={12} />
                                      <span>Edited</span>
                                    </div>
                                  </Tooltip>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-center align-middle text-gray-900">
                              {formatDate(date)}
                            </TableCell>
                            <TableCell className="text-center align-middle text-gray-700">
                              {formatTime(inTime)}
                            </TableCell>
                            <TableCell className="text-center align-middle text-gray-700">
                              {formatTime(breakIn)}
                            </TableCell>
                            <TableCell className="text-center align-middle text-gray-700">
                              {formatTime(breakOut)}
                            </TableCell>
                            <TableCell className="text-center align-middle font-medium text-teal-600">
                              {formatTime(outTime)}
                            </TableCell>
                            <TableCell className="text-center align-middle text-orange-600">
                              {breakHours}
                            </TableCell>
                            <TableCell className="text-center align-middle font-medium text-blue-600">
                              {workingHours === null || workingHours === ""
                                ? "-"
                                : `${workingHours}h`}
                            </TableCell>
                            <TableCell className="text-center align-middle">
                              <span className={getStatusBadgeClass(status)}>
                                {status}
                              </span>
                            </TableCell>
                            {userCanManageAttendance && (
                              <TableCell className="text-center align-middle">
                                <Popconfirm
                                  title="Are you sure you want to delete this attendance record?"
                                  onConfirm={() =>
                                    handleDeleteAttendance(recordId)
                                  }
                                  okText="Yes"
                                  cancelText="No"
                                  okButtonProps={{
                                    danger: true,
                                  }}
                                >
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 text-red-600 hover:bg-red-50 hover:text-red-700"
                                    title="Delete Attendance"
                                  >
                                    <Trash className="h-4 w-4" />
                                  </Button>
                                </Popconfirm>
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>
              {/* Pagination - Always show if there are records or loading */}
              <div className="flex items-center justify-end border-t p-4">
                <Pagination
                  current={currentPage}
                  pageSize={pageSize}
                  total={totalCount}
                  onChange={(page, size) => {
                    // This will trigger a new API call because currentPage/pageSize are in queryKey
                    if (size && size !== pageSize) {
                      // Reset to page 1 when page size changes
                      setCurrentPage(1);
                      setPageSize(size);
                    } else {
                      setCurrentPage(page);
                    }
                  }}
                  onShowSizeChange={(_current, size) => {
                    // Handle page size change separately
                    setCurrentPage(1);
                    setPageSize(size);
                  }}
                  showSizeChanger
                  showQuickJumper
                  showTotal={(total, range) =>
                    `${range[0]}-${range[1]} of ${total} items`
                  }
                  pageSizeOptions={["50", "100", "200", "300"]}
                  disabled={isLoading}
                />
              </div>
            </CardContent>
          </Card>

          {/* Add Manual Attendance Modal */}
          <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
            <DialogContent className="bg-white sm:max-w-[425px]">
              <DialogHeader>
                <DialogTitle>Add Manual Attendance</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label
                    htmlFor="modal-employee"
                    className="text-sm text-gray-700"
                  >
                    Employee (Optional)
                  </Label>
                  <Select
                    id="modal-employee"
                    allowClear
                    value={modalEmployeeId || undefined}
                    onChange={value => setModalEmployeeId(value || "")}
                    placeholder="Select employee"
                    className="w-full"
                    showSearch
                    filterOption={(input, option) => {
                      const label = String(option?.label ?? "");
                      return label.toLowerCase().includes(input.toLowerCase());
                    }}
                    disabled={userIsEmployee} // Disable if user is employee (locked to their own ID)
                    options={
                      employeeOptionsWithNames?.map(
                        (employee: { value: string; label: string }) => ({
                          value: employee.value,
                          label: employee.label,
                        })
                      ) ?? []
                    }
                    getPopupContainer={triggerNode =>
                      triggerNode.parentElement ?? document.body
                    }
                    dropdownStyle={{ zIndex: 1050 }}
                    virtual={false}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="modal-date" className="text-sm text-gray-700">
                    Date
                  </Label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                    <input
                      type="date"
                      id="modal-date"
                      value={modalDate}
                      onChange={e => setModalDate(e.target.value || "")}
                      className="h-[2.1rem] w-full rounded-lg border bg-[#fafafa] px-3 py-2 pl-10 text-sm"
                    />
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsModalOpen(false);
                    setModalEmployeeId("");
                    setModalDate("");
                  }}
                  disabled={generateAttendanceMutation.isPending}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleAddAttendance}
                  disabled={generateAttendanceMutation.isPending || !modalDate}
                  className="bg-primary hover:bg-primary/90 text-white"
                >
                  {generateAttendanceMutation.isPending
                    ? "Adding..."
                    : "Add Attendance"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </>
      )}
      {activeTab === "monthly" && (
        <>
          {attendanceReportTab === "monthly" && (
            <>
              {/* Page Header */}
              <div className="top-[-1.5rem] z-20 rounded-lg p-2 backdrop-blur-md md:sticky">
                <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
                  <div>
                    <h1 className="text-xl font-bold text-[#111827] md:text-2xl">
                      Monthly Attendance Report
                    </h1>
                    <p className="text-sm text-[#627384]">
                      Monthly attendance differences tracking
                    </p>
                  </div>
                  <div className="flex w-full flex-col gap-3 md:flex-row md:items-center md:justify-end xl:w-auto">
                    <Tabs
                      value={attendanceReportTab}
                      onValueChange={v =>
                        setAttendanceReportTab(v as "monthly" | "yearly")
                      }
                      className="w-full md:max-w-[320px]"
                    >
                      <TabsList className="inline-flex w-full border border-gray-200 bg-white">
                        <TabsTrigger
                          value="monthly"
                          className="text-primary flex w-full items-center justify-center gap-2 data-[state=active]:bg-white data-[state=active]:text-gray-900"
                        >
                          <Building2 size={15} />
                          Monthly
                        </TabsTrigger>
                        <TabsTrigger
                          value="yearly"
                          className="text-primary flex w-full items-center justify-center gap-2 data-[state=active]:bg-white data-[state=active]:text-gray-900"
                        >
                          <User size={15} />
                          Yearly
                        </TabsTrigger>
                      </TabsList>
                    </Tabs>
                    <Button
                      onClick={handleExportMonthly}
                      className="bg-primary hover:bg-primary/90 flex items-center gap-2 text-white"
                    >
                      <Download className="h-4 w-4" />
                      Export Report
                    </Button>
                  </div>
                </div>
              </div>

              {/* Filter Section */}
              <Card className="border border-gray-200 bg-white">
                <CardContent className="p-3 md:p-4">
                  <div className="grid grid-cols-1 gap-2 md:grid-cols-5 md:gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="month" className="text-sm text-gray-700">
                        Month
                      </Label>
                      <Select
                        id="month"
                        allowClear
                        value={selectedMonth}
                        onChange={setSelectedMonth}
                        placeholder="Select month"
                        className="h-[2rem] w-full rounded-xl"
                        options={[
                          { value: "January", label: "January" },
                          { value: "February", label: "February" },
                          { value: "March", label: "March" },
                          { value: "April", label: "April" },
                          { value: "May", label: "May" },
                          { value: "June", label: "June" },
                          { value: "July", label: "July" },
                          { value: "August", label: "August" },
                          { value: "September", label: "September" },
                          { value: "October", label: "October" },
                          { value: "November", label: "November" },
                          { value: "December", label: "December" },
                        ]}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="year" className="text-sm text-gray-700">
                        Year
                      </Label>
                      <input
                        type="number"
                        id="year"
                        value={selectedYear}
                        onChange={e =>
                          setSelectedYear(
                            Number(e.target.value) || new Date().getFullYear()
                          )
                        }
                        min="2000"
                        max="2100"
                        className="h-[2rem] w-full rounded-lg border bg-[#fafafa] px-3 text-sm"
                      />
                    </div>
                    {/* Employee filter - Only show for admin/user roles, hide for employee role */}
                    {!userIsEmployee && (
                      <div className="space-y-2">
                        <Label
                          htmlFor="monthly-employee"
                          className="text-sm text-gray-700"
                        >
                          Employee
                        </Label>
                        <Select
                          id="monthly-employee"
                          allowClear
                          value={monthlyEmployee}
                          onChange={setMonthlyEmployee}
                          placeholder="Select employee"
                          className="h-[2rem] w-full rounded-xl"
                          showSearch
                          filterOption={(input, option) => {
                            const label = String(option?.label ?? "");
                            return label
                              .toLowerCase()
                              .includes(input.toLowerCase());
                          }}
                          options={[
                            { value: "all", label: "All Employees" },
                            ...(employeeOptionsWithNames?.map(
                              (employee: { value: string; label: string }) => ({
                                value: employee.value,
                                label: employee.label,
                              })
                            ) ?? []),
                          ]}
                        />
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              <div className="grid grid-cols-1 gap-4">
                <Card className="border border-gray-200 bg-white">
                  <CardContent className="p-3 md:p-5">
                    <div className="flex flex-col gap-4">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <p className="text-sm font-semibold text-gray-800">
                            Present Rate
                          </p>
                          <div className="mt-2 flex items-end gap-2">
                            <p className="text-3xl font-bold text-green-600">
                              {monthlySummaryStats.presentRate.toFixed(2)}%
                            </p>
                            <span className="mb-1 text-sm text-gray-500">
                              Absent {monthlySummaryStats.absentRate.toFixed(2)}
                              %
                            </span>
                          </div>
                        </div>
                        <div className="rounded-full bg-green-50 p-3">
                          <Users className="h-6 w-6 text-green-600" />
                        </div>
                      </div>

                      <div className="space-y-2">
                        {/* <div className="flex items-center justify-between text-xs text-gray-500">
                      <span>Monthly presence overview</span>
                      <span>
                        {monthlySummaryStats.totalPresentDays} present day
                        {monthlySummaryStats.totalPresentDays !== 1 ? "s" : ""}
                      </span>
                    </div> */}
                        <Progress
                          value={monthlySummaryStats.presentRate}
                          className="h-2 bg-red-100"
                        />
                        <div className="flex items-center justify-between text-xs text-gray-500">
                          <span>
                            {monthlySummaryStats.totalDaysUptoToday} working day
                            {monthlySummaryStats.totalDaysUptoToday !== 1
                              ? "s"
                              : ""}{" "}
                            counted
                          </span>
                          {/* <span>
                        {monthlySummaryStats.totalPossibleDays} possible day
                        {monthlySummaryStats.totalPossibleDays !== 1 ? "s" : ""}
                      </span> */}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Monthly Differences Table (Employee Name, T.W.H., date columns) */}
              <Card className="border border-gray-200 bg-white">
                <CardContent className="p-0">
                  <div className="flex flex-col gap-4 border-b border-gray-200 p-4 sm:flex-row sm:items-center sm:justify-between">
                    <h2 className="text-primary text-lg font-bold">
                      Monthly Differences
                    </h2>
                    <span className="text-sm text-gray-500">
                      {monthlyRecords.length} Record
                      {monthlyRecords.length !== 1 ? "s" : ""}
                    </span>
                  </div>

                  <div className="w-full overflow-x-auto overflow-y-visible">
                    <Table className="min-w-[800px] border-collapse">
                      <TableHeader>
                        <TableRow className="bg-gray-50">
                          <TableHead className="left-0 z-10 w-10 border-b border-r border-gray-200 bg-gray-50 text-center font-semibold text-gray-700 sm:sticky">
                            #
                          </TableHead>
                          <TableHead className="left-10 z-10 min-w-[140px] border-b border-r border-gray-200 bg-gray-50 text-start font-semibold text-gray-700 sm:sticky">
                            Employee Name
                          </TableHead>
                          <TableHead className="left-[180px] z-10 min-w-[80px] border-b border-r border-gray-200 bg-gray-50 text-center font-semibold text-gray-700 sm:sticky">
                            T.W.H.
                          </TableHead>
                          {monthlyDiffDateColumnMeta.map(
                            ({ rawKey, display }) => (
                              <TableHead
                                key={rawKey}
                                className="min-w-[90px] border-b border-r border-gray-200 text-center text-xs font-semibold text-gray-700"
                              >
                                {display}
                              </TableHead>
                            )
                          )}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {monthlyLoading ? (
                          <TableRow>
                            <TableCell
                              colSpan={3 + monthlyDiffDateColumnMeta.length}
                              className="border-b border-gray-200 py-8 text-center text-gray-500"
                            >
                              Loading monthly attendance data...
                            </TableCell>
                          </TableRow>
                        ) : monthlyRecords.length === 0 ? (
                          <TableRow>
                            <TableCell
                              colSpan={3 + monthlyDiffDateColumnMeta.length}
                              className="border-b border-gray-200 py-8 text-center text-gray-500"
                            >
                              No monthly attendance records found
                            </TableCell>
                          </TableRow>
                        ) : (
                          monthlyRecords.map((record: any, index: number) => {
                            const employeeId =
                              record?.emp_id ??
                              record?.employee_id ??
                              record?.employee ??
                              "";
                            const employeeName =
                              record?.emp_name ??
                              (() => {
                                const names = getEmployeeNamesById(employeeId);
                                return (
                                  `${names.firstName} ${names.lastName}`.trim() ||
                                  "-"
                                );
                              })();
                            const monthlyDiff =
                              record?.monthly_diff ??
                              record?.monthly_difference ??
                              record?.difference ??
                              "-";
                            const isNeg =
                              typeof monthlyDiff === "string" &&
                              monthlyDiff.startsWith("-");
                            const isZero =
                              monthlyDiff === "0" ||
                              monthlyDiff === "0:00" ||
                              monthlyDiff === "00:00" ||
                              monthlyDiff === "+00:00" ||
                              monthlyDiff === "-00:00";

                            return (
                              <TableRow
                                key={employeeId ?? index}
                                className="group hover:bg-gray-50/80"
                              >
                                <TableCell className="left-0 z-10 w-10 border-b border-r border-gray-200 bg-white text-center text-sm text-gray-600 group-hover:bg-gray-50/80 sm:sticky">
                                  {index + 1}
                                </TableCell>
                                <TableCell className="left-10 z-10 min-w-[140px] border-b border-r border-gray-200 bg-white font-medium text-gray-900 group-hover:bg-gray-50/80 sm:sticky">
                                  {employeeName &&
                                  employeeName !== "-" &&
                                  employeeId ? (
                                    <Link
                                      to={`/employee/${employeeId}`}
                                      className="text-primary hover:underline"
                                      onClick={e => e.preventDefault()}
                                    >
                                      {employeeName}
                                    </Link>
                                  ) : (
                                    (employeeName ?? "-")
                                  )}
                                </TableCell>
                                <TableCell className="left-[180px] z-10 min-w-[80px] border-b border-r border-gray-200 bg-white text-center font-medium group-hover:bg-gray-50/80 sm:sticky">
                                  {monthlyDiff !== "-" &&
                                  monthlyDiff !== null ? (
                                    <span
                                      className={
                                        isZero
                                          ? "text-gray-900"
                                          : isNeg
                                            ? "text-red-600"
                                            : "text-green-600"
                                      }
                                    >
                                      {typeof monthlyDiff === "string" &&
                                      monthlyDiff.includes(":")
                                        ? monthlyDiff
                                        : formatHoursToHoursMinutes(
                                            monthlyDiff
                                          )}
                                    </span>
                                  ) : (
                                    "-"
                                  )}
                                </TableCell>
                                {monthlyDiffDateColumnMeta.map(({ rawKey }) => {
                                  const val =
                                    (record[rawKey] !== null &&
                                      String(record[rawKey]).trim()) ||
                                    "";
                                  const isOnLeave = /on-leave|on leave/i.test(
                                    val
                                  );
                                  const isAbsent =
                                    val === "A" || /^absent$/i.test(val);
                                  const isWeekend =
                                    val === "Saturday" || val === "Sunday";
                                  const cellClass = isOnLeave
                                    ? "text-amber-600 font-medium"
                                    : isAbsent
                                      ? "text-red-600 font-medium"
                                      : isWeekend
                                        ? "text-gray-500"
                                        : "text-gray-900";
                                  return (
                                    <TableCell
                                      key={rawKey}
                                      className={`min-w-[90px] border-b border-r border-gray-200 text-center text-sm ${cellClass}`}
                                    >
                                      {val || "-"}
                                    </TableCell>
                                  );
                                })}
                              </TableRow>
                            );
                          })
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            </>
          )}

          {attendanceReportTab === "yearly" && (
            <>
              <div className="sticky top-[-1.5rem] z-20 rounded-lg p-2 backdrop-blur-md">
                <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
                  <div>
                    <h1 className="text-2xl font-bold text-[#111827]">
                      Yearly Attendance Report
                    </h1>
                    <p className="text-sm text-[#627384]">
                      Year-wise attendance summary by employee
                    </p>
                  </div>
                  <div className="flex w-full flex-col gap-3 md:flex-row md:items-center md:justify-end xl:w-auto">
                    <Tabs
                      value={attendanceReportTab}
                      onValueChange={v =>
                        setAttendanceReportTab(v as "monthly" | "yearly")
                      }
                      className="w-full md:max-w-[320px]"
                    >
                      <TabsList className="inline-flex w-full border border-gray-200 bg-white">
                        <TabsTrigger
                          value="monthly"
                          className="text-primary flex w-full items-center justify-center gap-2 data-[state=active]:bg-white data-[state=active]:text-gray-900"
                        >
                          <Building2 size={15} />
                          Monthly
                        </TabsTrigger>
                        <TabsTrigger
                          value="yearly"
                          className="text-primary flex w-full items-center justify-center gap-2 data-[state=active]:bg-white data-[state=active]:text-gray-900"
                        >
                          <User size={15} />
                          Yearly
                        </TabsTrigger>
                      </TabsList>
                    </Tabs>
                    <Button
                      onClick={handleExportYearly}
                      disabled={!yearlyEmployee}
                      className="bg-primary hover:bg-primary/90 flex items-center gap-2 text-white"
                    >
                      <Download className="h-4 w-4" />
                      Export Report
                    </Button>
                  </div>
                </div>
              </div>

              <Card className="border border-gray-200 bg-white">
                <CardContent className="p-4">
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-5">
                    <div className="space-y-2">
                      <Label
                        htmlFor="yearly-year"
                        className="text-sm text-gray-700"
                      >
                        Year
                      </Label>
                      <input
                        type="number"
                        id="yearly-year"
                        value={yearlyYear}
                        onChange={e =>
                          setYearlyYear(
                            Number(e.target.value) || new Date().getFullYear()
                          )
                        }
                        min="2000"
                        max="2100"
                        className="h-[2rem] w-full rounded-lg border bg-[#fafafa] px-3 text-sm"
                      />
                    </div>
                    {!userIsEmployee && (
                      <div className="space-y-2">
                        <Label
                          htmlFor="yearly-employee"
                          className="text-sm text-gray-700"
                        >
                          Employee
                        </Label>
                        <Select
                          id="yearly-employee"
                          allowClear
                          value={yearlyEmployee || undefined}
                          onChange={value => setYearlyEmployee(value || "")}
                          placeholder="Select employee"
                          className="h-[2rem] w-full rounded-xl"
                          showSearch
                          filterOption={(input, option) => {
                            const label = String(option?.label ?? "");
                            return label
                              .toLowerCase()
                              .includes(input.toLowerCase());
                          }}
                          options={
                            employeeOptionsWithNames?.map(
                              (employee: { value: string; label: string }) => ({
                                value: employee.value,
                                label: employee.label,
                              })
                            ) ?? []
                          }
                        />
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card className="border border-gray-200 bg-white">
                <CardContent className="p-5">
                  {!yearlyEmployee ? (
                    <div className="py-8 text-center text-sm text-gray-500">
                      Please select an employee to view yearly attendance
                      report.
                    </div>
                  ) : yearlyLoading ? (
                    <div className="py-8 text-center text-sm text-gray-500">
                      Loading yearly attendance summary...
                    </div>
                  ) : (
                    <div className="space-y-5">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <p className="text-primary text-base font-semibold">
                            {yearlySummaryStats.employeeName}
                          </p>
                          <div className="mt-2 flex items-end gap-2">
                            <p className="text-3xl font-bold text-green-600">
                              {yearlySummaryStats.presentRate.toFixed(2)}%
                            </p>
                            <span className="mb-1 text-sm font-medium text-red-500">
                              Absent {yearlySummaryStats.absentRate.toFixed(2)}%
                            </span>
                          </div>
                        </div>
                        <div className="rounded-full bg-green-50 p-3">
                          <Users className="h-6 w-6 text-green-600" />
                        </div>
                      </div>

                      <Progress
                        value={yearlySummaryStats.presentRate}
                        className="h-2 bg-red-100"
                      />

                      <div className="grid grid-cols-1 gap-3 md:grid-cols-3 xl:grid-cols-4">
                        {/* <div className="rounded-lg border border-gray-200 bg-white p-4">
                          <p className="text-xs text-gray-500">
                            Total Employees
                          </p>
                          <p className="mt-1 text-2xl font-semibold text-gray-900">
                            {yearlySummaryStats.totalEmployees}
                          </p>
                        </div> */}
                        <div className="rounded-lg border border-gray-200 bg-white p-4">
                          <p className="text-xs text-gray-500">
                            Total Days Upto Today
                          </p>
                          <p className="mt-1 text-2xl font-semibold text-gray-900">
                            {yearlySummaryStats.totalDaysUptoToday}
                          </p>
                        </div>
                        <div className="rounded-lg border border-gray-200 bg-white p-4">
                          <p className="text-xs text-gray-500">
                            Total Possible Days
                          </p>
                          <p className="mt-1 text-2xl font-semibold text-gray-900">
                            {yearlySummaryStats.totalPossibleDays}
                          </p>
                        </div>
                        <div className="rounded-lg border border-gray-200 bg-green-50 p-4">
                          <p className="text-xs text-green-700">Present Days</p>
                          <p className="mt-1 text-2xl font-semibold text-green-700">
                            {yearlySummaryStats.totalPresentDays}
                          </p>
                        </div>
                        <div className="rounded-lg border border-gray-200 bg-red-50 p-4">
                          <p className="text-xs text-red-700">Absent Days</p>
                          <p className="mt-1 text-2xl font-semibold text-red-700">
                            {yearlySummaryStats.totalAbsentDays}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </>
      )}

      {/* Monthly Attendance Sheet tab */}
      {activeTab === "monthly-sheet" && (
        <>
          <div className="top-[-1.5rem] z-20 flex flex-col gap-3 rounded-lg p-2 backdrop-blur-md md:sticky md:flex-row md:items-center md:justify-between">
            <div>
              <h1 className="text-xl font-bold text-[#111827] md:text-2xl">
                Monthly Attendance Sheet
              </h1>
              <p className="text-sm text-[#627384]">
                Month-wise attendance with day columns (P, A, WO, H, HD, L)
              </p>
            </div>
            <Button
              onClick={handleExport}
              className="bg-primary hover:bg-primary/90 flex items-center gap-2 text-white"
            >
              <Download className="h-4 w-4" />
              Export Report
            </Button>
          </div>

          <Card className="border border-gray-200 bg-white">
            <CardContent className="p-3 md:p-4">
              <div className="grid grid-cols-1 gap-2 md:grid-cols-5 md:gap-4">
                <div className="space-y-2">
                  <Label
                    htmlFor="sheet-month"
                    className="text-sm text-gray-700"
                  >
                    Month
                  </Label>
                  <Select
                    id="sheet-month"
                    allowClear
                    value={selectedMonth}
                    onChange={setSelectedMonth}
                    placeholder="Select month"
                    className="h-[2rem] w-full rounded-xl"
                    options={[
                      { value: "January", label: "January" },
                      { value: "February", label: "February" },
                      { value: "March", label: "March" },
                      { value: "April", label: "April" },
                      { value: "May", label: "May" },
                      { value: "June", label: "June" },
                      { value: "July", label: "July" },
                      { value: "August", label: "August" },
                      { value: "September", label: "September" },
                      { value: "October", label: "October" },
                      { value: "November", label: "November" },
                      { value: "December", label: "December" },
                    ]}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="sheet-year" className="text-sm text-gray-700">
                    Year
                  </Label>
                  <input
                    type="number"
                    id="sheet-year"
                    value={selectedYear}
                    onChange={e =>
                      setSelectedYear(
                        Number(e.target.value) || new Date().getFullYear()
                      )
                    }
                    min="2000"
                    max="2100"
                    className="h-[2rem] w-full rounded-lg border bg-[#fafafa] px-3 text-sm"
                  />
                </div>
                {!userIsEmployee && (
                  <div className="space-y-2">
                    <Label
                      htmlFor="sheet-employee"
                      className="text-sm text-gray-700"
                    >
                      Employee
                    </Label>
                    <Select
                      id="sheet-employee"
                      allowClear
                      value={monthlyEmployee}
                      onChange={setMonthlyEmployee}
                      placeholder="Select employee"
                      className="h-[2rem] w-full rounded-xl"
                      showSearch
                      filterOption={(input, option) => {
                        const label = String(option?.label ?? "");
                        return label
                          .toLowerCase()
                          .includes(input.toLowerCase());
                      }}
                      options={[
                        { value: "all", label: "All Employees" },
                        ...(employeeOptionsWithNames?.map(
                          (employee: { value: string; label: string }) => ({
                            value: employee.value,
                            label: employee.label,
                          })
                        ) ?? []),
                      ]}
                    />
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="border border-gray-200 bg-white">
            <CardContent className="p-0">
              <div className="flex flex-col gap-4 border-b border-gray-200 p-4 sm:flex-row sm:items-center sm:justify-between">
                <h2 className="text-primary text-lg font-bold">
                  Monthly Attendance Sheet
                </h2>
                <span className="text-sm text-gray-500">
                  {attendanceSheetEmployees.length} Employee
                  {attendanceSheetEmployees.length !== 1 ? "s" : ""}
                </span>
              </div>

              <div className="w-full overflow-x-auto overflow-y-visible">
                <Table className="min-w-[800px] border-collapse">
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="left-0 z-10 w-10 border-b border-r border-gray-200 bg-gray-50 text-center font-semibold text-gray-700 sm:sticky">
                        #
                      </TableHead>
                      <TableHead className="left-10 z-10 min-w-[140px] border-b border-r border-gray-200 bg-gray-50 text-start font-semibold text-gray-700 sm:sticky">
                        Employee Name
                      </TableHead>
                      {attendanceSheetDayColumns.map(key => (
                        <TableHead
                          key={key}
                          className="min-w-[56px] border-b border-r border-gray-200 text-center text-xs font-semibold text-gray-700"
                        >
                          {key}
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {attendanceSheetLoading ? (
                      <TableRow>
                        <TableCell
                          colSpan={2 + attendanceSheetDayColumns.length}
                          className="border-b border-gray-200 py-8 text-center text-gray-500"
                        >
                          Loading attendance sheet...
                        </TableCell>
                      </TableRow>
                    ) : attendanceSheetEmployees.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={2 + attendanceSheetDayColumns.length}
                          className="border-b border-gray-200 py-8 text-center text-gray-500"
                        >
                          No attendance sheet data for this month
                        </TableCell>
                      </TableRow>
                    ) : (
                      attendanceSheetEmployees.map((emp, index) => (
                        <TableRow
                          key={emp.empname ?? index}
                          className="group hover:bg-gray-50/80"
                        >
                          <TableCell className="left-0 z-10 w-10 border-b border-r border-gray-200 bg-white text-center text-sm text-gray-600 group-hover:bg-gray-50/80 sm:sticky">
                            {index + 1}
                          </TableCell>
                          <TableCell className="left-10 z-10 min-w-[140px] border-b border-r border-gray-200 bg-white font-medium text-gray-900 group-hover:bg-gray-50/80 sm:sticky">
                            {emp.empname ?? "-"}
                          </TableCell>
                          {attendanceSheetDayColumns.map(dayKey => {
                            const code = emp.dates?.[dayKey] ?? "-";
                            const colorClass =
                              code === "P"
                                ? "text-green-600 font-medium"
                                : code === "A"
                                  ? "text-red-600 font-medium"
                                  : code === "WO" || code === "H"
                                    ? "text-gray-900"
                                    : code === "HD"
                                      ? "text-amber-600 font-medium"
                                      : code === "L"
                                        ? "text-blue-600 font-medium"
                                        : "text-gray-400";
                            return (
                              <TableCell
                                key={dayKey}
                                className={`min-w-[56px] border-b border-r border-gray-200 text-center text-sm ${colorClass}`}
                              >
                                {code}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default AttendancePage;
